/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import { PAMediaBatch } from "../../mediaRemoval/mediaRemoval.types";

interface Stats {
	total: number;
	eligible: number;
	rejected: number;
}

interface Props {
	stats: Stats;
	batchInfo?: PAMediaBatch;
	selectedBatch?: PAMediaBatch;
	setSelectedBatch: (batch: PAMediaBatch) => void;
	mediaBatches: PAMediaBatch[];
  errors: {
		batch: string;
		autoclave: string;
		cycleNumber: string;
		temperature: string;
		pressure: string;
		startDate: string;
		endDate: string;
		table: string;
	};

	setErrors: React.Dispatch<
		React.SetStateAction<{
			batch: string;
			autoclave: string;
			cycleNumber: string;
			temperature: string;
			pressure: string;
			startDate: string;
			endDate: string;
			table: string;
		}>
	>;
}

const ESHeader = ({ stats, batchInfo, selectedBatch, setSelectedBatch, mediaBatches, errors, setErrors, }: Props) => {
	const { t } = useTranslation();
	return (
		<div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="px-8 py-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center shadow-inner">
                <span className="material-symbols-outlined text-orange-600 text-3xl">sanitizer</span>
              </div>
              <div>
							<h2 className="text-xl font-black text-[#1E293B] tracking-tight">{t("postAutoclaveProcess.emptySterilization.title")}</h2>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{t("postAutoclaveProcess.emptySterilization.subtext")}</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">{t("postAutoclaveProcess.emptySterilization.selectMediaBatch")}  <span className="text-red-500 ml-1">*</span></label>
                <div className="relative group">
                <select
                    value={selectedBatch?.id?? ""}
                    onChange={(e) => {
                        const batch = mediaBatches.find(
                            (b) => b.id === e.target.value
                        );

                        if (batch) {
                            setSelectedBatch(batch);

                            setErrors((prev) => ({
                                ...prev,
                                batch: "",
                            }));
                        }
                    }}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] text-slate-700 outline-none focus:border-[#FF7043] transition-all"
                >
                    <option value="" disabled>
                        {t("postAutoclaveProcess.physicalInspection.selectAMediaBatch")}
                    </option>

                    {mediaBatches.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                            {batch.media_batch_identifier} — {batch.media_batch_description}
                        </option>
                    ))}
                </select>
            </div>
                {errors.batch && (
                    <p className="mt-1 text-[11px] text-red-500">
                        {errors.batch}
                    </p>
                )}
              </div>

              <div className="px-4 py-2 bg-orange-50 border border-orange-100 rounded-xl flex items-center gap-2 mt-4">
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                <span className="text-[10px] font-black text-orange-600 uppercase tracking-widest">{t("postAutoclaveProcess.emptySterilization.awaitingEmptySterilization")}</span>
              </div>
            </div>
          </div>

          {/* Batch Info Bar */}
          <div className="flex items-center gap-4">
            <div className="flex-1 grid grid-cols-5 gap-4">
              {[
                { label: t("postAutoclaveProcess.emptySterilization.batchId"), value: batchInfo?.media_batch_identifier, icon: 'tag' },
                { label: t("postAutoclaveProcess.emptySterilization.mediaName"), value: batchInfo?.media_type, icon: 'science' },
                { label: t("postAutoclaveProcess.emptySterilization.activity"), value: "Sterilization", icon: 'assignment' },
                { label: t("postAutoclaveProcess.emptySterilization.totalEligible"), value: stats.eligible, icon: 'inventory_2', color: 'text-emerald-500' },
                { label: t("postAutoclaveProcess.emptySterilization.rejected"), value: stats.rejected, icon: 'cancel', color: 'text-red-500' }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-100 rounded-2xl px-4 py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <span className={`material-symbols-outlined text-slate-400 text-lg ${item.color || ''}`}>{item.icon}</span>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.label}</p>
                    <p className="text-xs font-black text-[#1E293B] truncate">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
	);
};

export default ESHeader;
